/**
 * Enhanced MCP Flow Integration Component
 * Clean, dynamic UI with sidebar pattern for better organization
 */

import React, {
  useState, useEffect, useRef, useCallback, useReducer, createPortal,
} from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  CircularProgress,
  Badge,
  Stack,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Collapse,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Cloud as CloudIcon,
  Memory as MemoryIcon,
  Timeline as TimelineIcon,
  PlayArrow as PlayIcon,
  Sync as SyncIcon,
  Stream as StreamIcon,
  Analytics as AnalyticsIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  BugReport as BugReportIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import MCPService from '../../services/mcpService';
import axiosClient from '../../api/axios.client';
import { useFlowContext } from '../../contexts/FlowContext';

function MCPFlowIntegration({
  flowId, isTabMode = false, onContextChange, onToolExecuted,
}) {
  const mcpService = MCPService; // Use the singleton instance
  const [connectedServers, setConnectedServers] = useState([]);
  const [availableTools, setAvailableTools] = useState({});

  // Debug available tools
  useEffect(() => {
    // Log only when tools change significantly
    if (Object.keys(availableTools).length > 0) {
      console.log('🔧 Available tools updated:', Object.keys(availableTools).length, 'servers with tools');
    }
  }, [availableTools]);
  const [contextStats, setContextStats] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [isOpen, setIsOpen] = useState(false); // Sidebar state
  const [activeView, setActiveView] = useState('overview'); // Current view in sidebar
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    servers: 0,
    lastSync: null,
  });
  const [renderCount, setRenderCount] = useState(0);
  const [servers, setServers] = useState([]);
  const [configDialog, setConfigDialog] = useState({
    open: false,
    server: null,
    apiKey: '',
    _debugTimestamp: null, // Add debug field to track state changes
  });

  // Force update mechanism using useReducer
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // Initialize window variables for tracking
  if (typeof window.lastLoggedDialogState === 'undefined') {
    window.lastLoggedDialogState = false;
  }
  if (typeof window.lastDialogState === 'undefined') {
    window.lastDialogState = false;
  }

  // Debug wrapper for setConfigDialog to track state changes (reduced logging)
  const debugSetConfigDialog = useCallback((newState) => {
    const timestamp = new Date().toISOString();

    // Only log when dialog is opening/closing to reduce console spam
    if (newState.open !== configDialog.open) {
      console.log('🔧 Dialog state change:', {
        action: newState.open ? 'OPENING' : 'CLOSING',
        server: newState.server?.name || 'null',
        timestamp: timestamp.slice(-8), // Just show time part
      });
    }

    setConfigDialog({
      ...newState,
      _debugTimestamp: timestamp,
    });
  }, [configDialog.open]);

  // Store React state globally for external access (minimal, runs only once)
  useEffect(() => {
    // Store React state globally for external access (minimal)
    window.reactState = {
      configDialog: { open: configDialog.open, server: configDialog.server?.id || null },
      servers: servers?.length || 0,
      timestamp: new Date().toISOString(),
    };
  }, [configDialog.open, configDialog.server?.id, servers?.length]);

  // Debug effect to track component renders (minimal logging)
  useEffect(() => {
    // Always increment render count but only log when needed
    setRenderCount((prev) => {
      const newCount = prev + 1;
      // Only log when dialog state changes to reduce console spam
      if (configDialog.open !== window.lastLoggedDialogState) {
        console.log('🔧 Component render:', {
          renderCount: newCount,
          dialogOpen: configDialog.open,
          timestamp: new Date().toISOString().slice(-8),
        });
        window.lastLoggedDialogState = configDialog.open;
      }
      return newCount;
    });
  }, [configDialog.open]); // Add proper dependency

  // Debug effect to track dialog state changes (minimal)
  useEffect(() => {
    // Only log when dialog state actually changes
    if (configDialog.open !== window.lastDialogState) {
      window.lastDialogState = configDialog.open;

      console.log('🔧 Dialog state:', {
        open: configDialog.open,
        server: configDialog.server?.name || 'null',
        timestamp: configDialog._debugTimestamp?.slice(-8),
      });

      // Only do DOM check when dialog opens
      if (configDialog.open) {
        console.log('🔧 Dialog opened, checking DOM...');

        // Quick DOM check after a short delay
        setTimeout(() => {
          const allDialogs = document.querySelectorAll('[role="dialog"], #debug-dialog, #emergency-debug-dialog');
          const dialogFound = allDialogs.length > 0;

          console.log('🔧 Dialog visibility check (Portal):', {
            dialogsFound: allDialogs.length,
            dialogVisible: dialogFound,
            timestamp: new Date().toISOString().slice(-8),
            portals: {
              debugDialog: !!document.getElementById('debug-dialog'),
              emergencyDialog: !!document.getElementById('emergency-debug-dialog'),
              materialDialog: !!document.querySelector('[role="dialog"]'),
            },
          });
        }, 200);
      }
    }
  }, [configDialog.open]); // Remove problematic dependencies

  // Dialog visibility is handled by Material-UI - no manual checking needed

  const context = useFlowContext();

  // Check if context is available
  if (!context) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          <Typography variant="h6">Flow Context Unavailable</Typography>
          <Typography variant="body2">
            The flow context is not available. Make sure you're using this component within a FlowContextProvider.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const {
    getVar, setVar, getAllVars, getMCPServerInfo,
  } = context;

  // Function to check actual MCP server connections
  const checkMCPServerConnections = useCallback(async () => {
    try {
      setIsLoading(true);

      // Test the standalone MCP server directly
      const connectedServers = [];

      try {
        console.log('🔍 Checking MCP server connection...');
        // Test health endpoint of standalone MCP server
        const response = await fetch('/mcp-server/health', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        console.log('🔍 MCP Server response status:', response.status);

        if (response.ok) {
          const healthData = await response.json();

          connectedServers.push({
            id: 'local-mcp-server',
            name: 'Local MCP Server',
            status: 'connected',
            tools: healthData.server.available_tools.length,
            lastConnected: new Date().toISOString(),
            serverInfo: healthData.server,
          });

          console.log('✅ MCP Server connected:', healthData.server.name, 'with', healthData.server.available_tools.length, 'tools');
        } else {
          console.error('❌ MCP Server response not OK:', response.status, response.statusText);
          connectedServers.push({
            id: 'local-mcp-server',
            name: 'Local MCP Server',
            status: 'error',
            tools: 0,
            lastError: `HTTP ${response.status}: ${response.statusText}`,
          });
        }
      } catch (error) {
        console.error('❌ MCP Server connection failed:', error);
        connectedServers.push({
          id: 'local-mcp-server',
          name: 'Local MCP Server',
          status: 'disconnected',
          tools: 0,
          lastError: error.message,
        });
      }

      setConnectedServers(connectedServers);
      setConnectionStatus({
        connected: connectedServers.some((s) => s.status === 'connected'),
        servers: connectedServers.filter((s) => s.status === 'connected').length,
        lastSync: new Date().toISOString(),
      });

      console.log('✅ MCP Server connections checked:', connectedServers);

      // Also load available tools if connected
      if (connectedServers.some((s) => s.status === 'connected')) {
        await loadMCPData();
      }
    } catch (error) {
      console.error('❌ Failed to check MCP server connections:', error);
      setConnectionStatus({
        connected: false,
        servers: 0,
        lastSync: new Date().toISOString(),
        error: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setConnectedServers, setConnectionStatus]);

  // Function to load MCP data when connection is established
  const loadMCPData = async () => {
    try {
      // Load available tools from the connected MCP server
      const toolsResponse = await fetch('/mcp-server/tools');

      if (toolsResponse.ok) {
        const toolsData = await toolsResponse.json();
        const tools = toolsData.tools || toolsData.server?.available_tools || [];

        // Update available tools state
        const newAvailableTools = {
          'local-mcp-server': tools,
        };

        setAvailableTools(newAvailableTools);

        // Update servers state to trigger re-render
        setServers(mcpService.servers);

        // Load context data if available
        try {
          if (context && context.getAllVars) {
            const allVars = context.getAllVars();
            console.log('✅ Loaded context data:', Object.keys(allVars).length, 'variables');
          }
        } catch (error) {
          console.error('❌ Failed to load context data:', error);
        }
      } else {
        console.error('❌ Tools response not OK:', toolsResponse.status, toolsResponse.statusText);
      }
    } catch (error) {
      console.error('❌ Failed to load MCP data:', error);
    }
  };

  // Debug function to test connection manually
  const testConnection = async () => {
    console.log('🔍 Testing MCP connection manually...');
    try {
      const response = await fetch('/mcp-server/health', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      console.log('🔍 Manual test response:', response.status, response.statusText);
      const data = await response.json();
      console.log('🔍 Manual test data:', data);
      alert(`MCP Server Response: ${response.status} - ${data.status}`);
    } catch (error) {
      console.error('🔍 Manual test failed:', error);
      alert(`MCP Connection Error: ${error.message}`);
    }
  };

  // Function to toggle server enabled/disabled state
  const toggleServer = async (serverId) => {
    try {
      // Find the server in the list
      const serverIndex = mcpService.servers.findIndex((s) => s.id === serverId);
      if (serverIndex === -1) {
        console.error('🔧 Server not found:', serverId);
        return;
      }

      const currentServer = mcpService.servers[serverIndex];

      // Check if server requires API key and doesn't have one
      if (currentServer.enabled === false && (!currentServer.authToken || currentServer.authToken === 'YOUR_OPENWEATHERMAP_API_KEY' || currentServer.authToken === 'YOUR_GITHUB_TOKEN' || currentServer.authToken === 'YOUR_NEWSAPI_KEY' || currentServer.authToken === null || currentServer.authToken.trim() === '')) {
        console.log('🔧 Server needs configuration:', currentServer.name, currentServer.id);
        console.log('🔧 Setting config dialog state...');
        debugSetConfigDialog({
          open: true,
          server: currentServer,
          apiKey: '',
        });
        console.log('🔧 Config dialog state should be set now');

        // Force a state update to ensure it takes effect
        setTimeout(() => {
          console.log('🔧 Dialog state check:', {
            open: configDialog.open,
            server: configDialog.server?.name || 'null',
            timestamp: configDialog._debugTimestamp?.slice(-8),
          });
        }, 100);
        return;
      }

      // Toggle the enabled state
      const updatedServer = {
        ...currentServer,
        enabled: !currentServer.enabled,
      };

      // Update the server
      await mcpService.updateServer(serverId, { enabled: updatedServer.enabled });

      // Update local servers state to trigger re-render
      setServers((prev) => prev.map((s) => (s.id === serverId ? updatedServer : s)));

      // If enabling the server, load its tools
      if (updatedServer.enabled) {
        await loadMCPData();
      } else {
        // If disabling, remove tools from availableTools
        setAvailableTools((prev) => {
          const newTools = { ...prev };
          delete newTools[serverId];
          return newTools;
        });
      }
    } catch (error) {
      console.error('🔧 Failed to toggle server:', error);
    }
  };

  // Function to save API key and enable server
  const handleSaveAPIKey = async () => {
    try {
      if (!configDialog.server || !configDialog.apiKey.trim()) {
        console.error('🔧 Missing server or API key');
        return;
      }

      const updatedServer = {
        ...configDialog.server,
        enabled: true,
        authToken: configDialog.apiKey.trim(),
      };

      // Update the server via MCP service
      await mcpService.updateServer(configDialog.server.id, {
        enabled: true,
        authToken: configDialog.apiKey.trim(),
      });

      // Update local servers state to trigger re-render
      setServers((prev) => prev.map((s) => (s.id === configDialog.server.id ? updatedServer : s)));

      // Load tools for the enabled server
      await loadMCPData();

      // Close dialog
      debugSetConfigDialog({ open: false, server: null, apiKey: '' });
    } catch (error) {
      console.error('🔧 Failed to save API key:', error);
    }
  };

  // Early return if MCP service is not available
  if (!mcpService || typeof mcpService.wsConnected === 'undefined') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          <Typography variant="h6">MCP Integration Unavailable</Typography>
          <Typography variant="body2">
            The MCP service is not properly configured. Please check the console for errors.
          </Typography>
        </Alert>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          Reload Page
        </Button>
      </Box>
    );
  }

  // Safe initialization - only run if component is properly mounted
  useEffect(() => {
    if (!mcpService || typeof mcpService.wsConnected === 'undefined') {
      console.warn('MCP Service not available, skipping initialization');
      return;
    }

    const init = async () => {
      try {
        setIsLoading(true);
        console.log('🔧 Initializing MCP Integration...');

        // Only initialize if not already connected
        if (!mcpService.wsConnected) {
          console.log('🔧 Initializing MCP service...');
          await mcpService.initialize();
        }

        console.log('🔧 Checking MCP server connections...');
        // Check MCP server connections
        await checkMCPServerConnections();

        await loadMCPData();

        // Initialize servers state
        if (mcpService?.servers) {
          setServers(mcpService.servers);
          console.log('🔧 Servers initialized:', mcpService.servers.length, 'servers');
        } else {
          console.warn('🔧 No servers found in mcpService');
        }

        // Set up basic event listeners if the method exists
        if (typeof setupEventListeners === 'function') {
          setupEventListeners();
        }

        setIsLoading(false);
      } catch (error) {
        console.error('❌ Failed to initialize MCP integration:', error);
        setIsLoading(false);
      }
    };

    init();
  }, [checkMCPServerConnections]); // Include checkMCPServerConnections in dependency array

  const initializeMCPIntegration = async () => {
    try {
      setIsLoading(true);

      // Initialize MCP service if not already done
      if (!mcpService.wsConnected) {
        await mcpService.initialize();
      }

      // Set up event listeners
      setupEventListeners();

      // Connect to available MCP servers
      await connectToMCPServers();

      // Start context streaming
      await startContextStreaming();

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to initialize MCP integration:', error);
      setIsLoading(false);
    }
  };

  const setupEventListeners = () => {
    // Listen for MCP server connections
    mcpService.addEventListener('server:connected', (data) => {
      setConnectedServers((prev) => [...prev, data.serverId]);
    });

    mcpService.addEventListener('server:disconnected', (data) => {
      setConnectedServers((prev) => prev.filter((id) => id !== data.serverId));
    });

    // Listen for tool executions
    mcpService.addEventListener('tool:executed', (data) => {
      onToolExecuted?.(data);
    });

    // Listen for context changes
    mcpService.addEventListener('context:changed', (data) => {
      onContextChange?.(data);
      updateContextStats();
    });

    // Listen for sync completion
    mcpService.addEventListener('sync:completed', (data) => {
      setLastSync(new Date().toISOString());
    });
  };

  const connectToMCPServers = async () => {
    try {
      const servers = mcpService.getAllServers();

      for (const server of servers) {
        try {
          await mcpService.connectToMCPServer(server);

          // Load available tools for this server
          const tools = await mcpService.getAvailableToolsWithCache(server.id);
          setAvailableTools((prev) => ({
            ...prev,
            [server.id]: tools,
          }));

          // Register tools with context manager
          for (const tool of tools) {
            mcpService.contextManager.registerMCPTool(server.id, tool);
          }
        } catch (error) {
          console.error(`Failed to connect to server ${server.name}:`, error);
        }
      }
    } catch (error) {
      console.error('Failed to connect to MCP servers:', error);
    }
  };

  const startContextStreaming = async () => {
    try {
      // Stream global context changes
      const globalStream = await mcpService.streamContext('global', (data) => {
        console.log('Global context changed:', data);
        // Update flow context
        if (data.key && data.value !== undefined) {
          setVar(data.key, data.value, 'flow');
        }
      });

      // Stream MCP-specific context changes
      const mcpStream = await mcpService.streamContext('mcp', (data) => {
        console.log('MCP context changed:', data);
        // Update MCP context in flow
        if (data.serverId && data.toolName) {
          setVar(`mcp_${data.serverId}_${data.toolName}`, data.value, 'mcp');
        }
      });

      // Store stream references
      streamRefs.current.set('global', globalStream);
      streamRefs.current.set('mcp', mcpStream);
    } catch (error) {
      console.error('Failed to start context streaming:', error);
    }
  };

  const executeMCPTool = async (serverId, toolName, args = {}) => {
    try {
      // Prepare context-enriched arguments with proper error handling
      const enrichedArgs = {
        ...args,
        flowId,
        nodeId: getVar('currentNodeId', 'flow') || null,
        userId: getVar('userId', 'session') || getVar('userId', 'global') || 'anonymous',
        context: {
          global: getAllVars ? getAllVars('global') : {},
          flow: getAllVars ? getAllVars('flow') : {},
          mcp: getAllVars ? getAllVars('mcp') : {},
        },
      };

      // Execute tool with enhanced context via backend API
      const result = await mcpService.executeToolWithEnhancedContext(serverId, toolName, enrichedArgs);

      // Update local context with result
      if (result && result.success) {
        setVar(`tool_result_${toolName}_${Date.now()}`, result, 'flow');

        // Broadcast context update to all connected clients
        await mcpService.broadcastContextUpdate('flow', `tool_result_${toolName}_${Date.now()}`, result);

        // Update tool usage statistics
        setVar(`tool_stats_${toolName}`, {
          lastUsed: new Date().toISOString(),
          usageCount: (getVar ? getVar(`tool_stats_${toolName}`, 'flow')?.usageCount || 0 : 0) + 1,
          serverId,
        }, 'flow');
      }

      return result;
    } catch (error) {
      console.error('Failed to execute MCP tool via backend:', error);

      // Fallback: Try to execute directly with the standalone MCP server
      try {
        console.log('🔄 Fallback: Executing tool directly with MCP server...');
        const directResult = await mcpService.executeTool(serverId, toolName, args);

        // Store result in context
        setVar(`tool_result_${toolName}_${Date.now()}`, directResult, 'flow');

        return directResult;
      } catch (fallbackError) {
        console.error('🔥 Fallback execution also failed:', fallbackError);
        throw error; // Throw original error
      }
    }
  };

  /**
   * Enhanced context synchronization with backend
   */
  const syncContextWithBackend = async () => {
    try {
      const contextData = {
        global: getAllVars('global'),
        flow: getAllVars('flow'),
        mcp: getAllVars('mcp'),
      };

      const syncResult = await mcpService.syncContextWithBackend(contextData, {
        flowId,
        version: contextStats.context?.version || 0,
      });

      if (syncResult.success) {
        setLastSync(syncResult.timestamp);
        console.log('✅ Context synchronized with backend');
      }

      return syncResult;
    } catch (error) {
      console.error('Failed to sync context with backend:', error);
      throw error;
    }
  };

  /**
   * Subscribe to backend event streams
   */
  const subscribeToBackendEvents = async () => {
    try {
      // Subscribe to context changes
      const contextSubscription = await mcpService.subscribeToBackendEvent('context:changed', {
        scope: 'flow',
        flow_id: flowId,
      });

      // Subscribe to flow execution updates
      const flowSubscription = await mcpService.subscribeToBackendEvent('flow:execution', {
        flow_id: flowId,
      });

      // Subscribe to tool usage metrics
      const toolSubscription = await mcpService.subscribeToBackendEvent('tool:usage', {
        flow_id: flowId,
      });

      return {
        contextSubscription,
        flowSubscription,
        toolSubscription,
        unsubscribe: async () => {
          await contextSubscription.unsubscribe();
          await flowSubscription.unsubscribe();
          await toolSubscription.unsubscribe();
        },
      };
    } catch (error) {
      console.error('Failed to subscribe to backend events:', error);
      throw error;
    }
  };

  /**
   * Get enhanced statistics from backend
   */
  const getEnhancedStats = async () => {
    try {
      const stats = await mcpService.getBackendStats();
      setContextStats((prev) => ({ ...prev, backend: stats }));
      return stats;
    } catch (error) {
      console.error('Failed to get enhanced stats:', error);
      return null;
    }
  };

  const syncContextWithMCP = async () => {
    try {
      await mcpService.syncAllContexts();
      console.log('Context synchronized with MCP servers');
    } catch (error) {
      console.error('Failed to sync context with MCP servers:', error);
    }
  };

  const updateContextStats = () => {
    const stats = mcpService.getUnifiedStats();
    setContextStats(stats);
  };

  const cleanup = async () => {
    // Stop all active streams
    for (const [streamId, stream] of streamRefs.current) {
      await stream.stop();
    }
    streamRefs.current.clear();
  };

  const getConnectionStatus = () => {
    const serverInfo = mcpService.contextManager.getMCPServerInfo();
    return {
      connected: serverInfo.connected,
      lastSync: serverInfo.lastSync,
      servers: connectedServers.length,
    };
  };

  // Sidebar Navigation Items
  const sidebarItems = [
    {
      id: 'overview', label: 'Overview', icon: <DashboardIcon />, color: 'primary',
    },
    {
      id: 'tools', label: 'MCP Tools', icon: <CloudIcon />, color: 'secondary',
    },
    {
      id: 'context', label: 'Context', icon: <MemoryIcon />, color: 'info',
    },
    {
      id: 'streaming', label: 'Streaming', icon: <TimelineIcon />, color: 'success',
    },
    {
      id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon />, color: 'warning',
    },
    {
      id: 'test', label: 'Test', icon: <BugReportIcon />, color: 'error',
    },
    {
      id: 'settings', label: 'Settings', icon: <SettingsIcon />, color: 'default',
    },
  ];

  const renderSidebar = () => (
    <Drawer
      variant="persistent"
      anchor="right"
      open={isOpen}
      sx={{
        width: 320,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 320,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div">
            MCP Control
          </Typography>
          <IconButton onClick={() => setIsOpen(false)} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Enhanced Integration
        </Typography>
      </Box>

      <List sx={{ p: 1 }}>
        {sidebarItems.map((item) => (
          <ListItem
            key={item.id}
            button
            selected={activeView === item.id}
            onClick={() => setActiveView(item.id)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: `${item.color}.main`, minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: activeView === item.id ? 600 : 400,
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  const renderMainContent = () => (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2,
    }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 400,
        }}
      >
        <CloudIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          MCP Integration Ready
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click the MCP button in the toolbar to open the control panel
        </Typography>
        <Button
          variant="contained"
          onClick={() => setIsOpen(true)}
          startIcon={<CloudIcon />}
        >
          Open MCP Panel
        </Button>
      </Paper>
    </Box>
  );

  const renderTabContent = () => (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Stack spacing={2}>
        {/* Connection Status */}
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">Connection Status</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  icon={connectionStatus.connected ? <SuccessIcon /> : <ErrorIcon />}
                  label={connectionStatus.connected ? 'Connected' : 'Disconnected'}
                  color={connectionStatus.connected ? 'success' : 'error'}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={checkMCPServerConnections}
                  disabled={isLoading}
                  title="Refresh Connection Status"
                >
                  {isLoading ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={testConnection}
                  title="Test Connection"
                >
                  <BugReportIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {connectionStatus.servers}
              {' '}
              servers connected
            </Typography>
            {connectionStatus.error && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                Error:
                {' '}
                {connectionStatus.error}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Navigation Pills */}
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Navigation
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {sidebarItems.map((item) => (
                <Chip
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  clickable
                  color={activeView === item.id ? 'primary' : 'default'}
                  variant={activeView === item.id ? 'filled' : 'outlined'}
                  onClick={() => setActiveView(item.id)}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Active View Content */}
        <Box sx={{ flex: 1 }}>
          {renderActiveView()}
        </Box>

        {/* Tools Preview */}
        {Object.keys(availableTools).length > 0 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Available Tools
              </Typography>
              <Stack spacing={1}>
                {Object.entries(availableTools).slice(0, 3).map(([serverId, tools]) => (
                  <Box key={serverId}>
                    <Typography variant="caption" color="text.secondary">
                      {serverId}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {tools.slice(0, 2).map((tool) => (
                        <Chip
                          key={tool.name}
                          label={tool.name}
                          size="small"
                          variant="outlined"
                          onClick={() => executeMCPTool(serverId, tool.name)}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

      </Stack>
    </Box>
  );

  const renderActiveView = () => {
    switch (activeView) {
      case 'overview':
        return renderOverview();
      case 'tools':
        return renderTools();
      case 'context':
        return renderContext();
      case 'streaming':
        return renderStreaming();
      case 'analytics':
        return renderAnalytics();
      case 'test':
        return renderTest();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  const renderSidePanelContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Sidebar */}
      <Box sx={{
        width: 200, borderRight: 1, borderColor: 'divider', flexShrink: 0,
      }}
      >
        <List sx={{ p: 1 }}>
          {sidebarItems.map((item) => (
            <ListItem
              key={item.id}
              button
              selected={activeView === item.id}
              onClick={() => setActiveView(item.id)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: activeView === item.id ? 600 : 400,
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {renderActiveView()}
      </Box>
    </Box>
  );

  const renderOverview = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        System Overview
      </Typography>

      {/* Quick Actions */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SyncIcon />}
          onClick={syncContextWithMCP}
          disabled={!connectionStatus.connected}
          fullWidth
        >
          Sync Context
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<StreamIcon />}
          onClick={() => setActiveView('streaming')}
          fullWidth
        >
          Start Streaming
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AnalyticsIcon />}
          onClick={() => setActiveView('analytics')}
          fullWidth
        >
          View Analytics
        </Button>
      </Stack>

      {/* System Stats */}
      <Card>
        <CardContent sx={{ p: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2">Servers:</Typography>
            <Typography variant="body2">{connectionStatus.servers}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2">Tools:</Typography>
            <Typography variant="body2">{Object.keys(availableTools).length}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Status:</Typography>
            <Chip
              size="small"
              label={connectionStatus.connected ? 'Connected' : 'Disconnected'}
              color={connectionStatus.connected ? 'success' : 'error'}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="body2">Config Dialog:</Typography>
            <Chip
              size="small"
              label={configDialog.open ? 'OPEN' : 'CLOSED'}
              color={configDialog.open ? 'success' : 'default'}
              variant={configDialog.open ? 'filled' : 'outlined'}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="body2">Render Count:</Typography>
            <Chip
              size="small"
              label={renderCount}
              color="info"
              variant="outlined"
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="body2">Dialog State:</Typography>
            <Chip
              size="small"
              label={configDialog.open ? 'OPEN' : 'CLOSED'}
              color={configDialog.open ? 'success' : 'error'}
              variant="filled"
            />
          </Box>
          <Button
            size="small"
            variant="contained"
            color="error"
            sx={{ mt: 1 }}
            onClick={() => {
              console.log('🔧 FORCE DIALOG TEST');
              debugSetConfigDialog({
                open: true,
                server: {
                  id: 'test-server',
                  name: 'Test Server',
                  description: 'This is a test server for dialog visibility',
                },
                apiKey: 'test',
              });
            }}
          >
            FORCE TEST DIALOG
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  const renderTools = () => (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">
          MCP Tools
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SyncIcon />}
          onClick={loadMCPData}
        >
          Refresh Tools
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => {
            console.log('🔧 Manual refresh of servers...');
            if (mcpService?.servers) {
              setServers([...mcpService.servers]);
              console.log('🔧 Servers refreshed:', mcpService.servers.length);
              console.log('🔧 All servers:', mcpService.servers.map((s) => ({
                id: s.id,
                name: s.name,
                enabled: s.enabled,
                authToken: s.authToken ? (s.authToken.length > 10 ? `${s.authToken.substring(0, 10)}...` : s.authToken) : 'null',
                hasConfigureButton: !s.enabled && (s.authToken === null || s.authToken === 'YOUR_OPENWEATHERMAP_API_KEY' || s.authToken === 'YOUR_GITHUB_TOKEN' || s.authToken === 'YOUR_NEWSAPI_KEY'),
              })));
            } else {
              console.warn('🔧 mcpService.servers is undefined');
            }
          }}
        >
          Refresh Servers
        </Button>
        <Button
          size="small"
          variant="contained"
          color="info"
          onClick={() => {
            console.log('🔍 Checking all servers for configure buttons...');
            if (mcpService?.servers) {
              mcpService.servers.forEach((server) => {
                const hasConfigureButton = !server.enabled && (!server.authToken || server.authToken === 'YOUR_OPENWEATHERMAP_API_KEY' || server.authToken === 'YOUR_GITHUB_TOKEN' || server.authToken === 'YOUR_NEWSAPI_KEY' || server.authToken === null || server.authToken.trim() === '');
                console.log(`🔍 ${server.name}: enabled=${server.enabled}, authToken=${server.authToken}, hasConfigureButton=${hasConfigureButton}`);
              });
            }
          }}
        >
          Debug Servers
        </Button>
      </Box>

      {/* Server Management Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            MCP Servers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enable/disable MCP servers to access their tools
          </Typography>

          {servers?.length === 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No servers configured
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  console.log('🔧 Force loading servers...');
                  if (mcpService) {
                    mcpService.servers = [];
                    mcpService.loadServers().then(() => {
                      setServers([...mcpService.servers]);
                      console.log('🔧 Servers force loaded:', mcpService.servers.length);
                    });
                  }
                }}
              >
                Load Servers
              </Button>
            </Box>
          ) : (
            <Stack spacing={1}>
              {servers.map((server) => (
                <Card key={server.id} variant="outlined" sx={{ p: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {server.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {server.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {server.host}
                        :
                        {server.port}
                        {' '}
                        (
                        {server.protocol}
                        )
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        size="small"
                        label={server.enabled ? 'Enabled' : 'Disabled'}
                        color={server.enabled ? 'success' : 'default'}
                        variant={server.enabled ? 'filled' : 'outlined'}
                      />
                      <Button
                        size="small"
                        variant={server.enabled ? 'outlined' : 'contained'}
                        color={server.enabled ? 'error' : 'primary'}
                        onClick={() => toggleServer(server.id)}
                      >
                        {server.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      {!server.enabled && (!server.authToken || server.authToken === 'YOUR_OPENWEATHERMAP_API_KEY' || server.authToken === 'YOUR_GITHUB_TOKEN' || server.authToken === 'YOUR_NEWSAPI_KEY' || server.authToken === null || server.authToken.trim() === '') && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => debugSetConfigDialog({
                          open: true,
                          server,
                          apiKey: '',
                        })}
                      >
                        Configure
                      </Button>
                      )}
                    </Box>
                  </Box>
                </Card>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {Object.keys(availableTools).length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No tools available
          </Typography>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={loadMCPData}
          >
            Load Tools
          </Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {Object.entries(availableTools).map(([serverId, tools]) => (
            <Card key={serverId}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {serverId}
                </Typography>
                <Stack spacing={0.5}>
                  {tools.map((tool) => (
                    <Button
                      key={tool.name}
                      size="small"
                      variant="outlined"
                      startIcon={<PlayIcon />}
                      onClick={() => executeMCPTool(serverId, tool.name)}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap>
                          {tool.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {tool.description}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderContext = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Context Management
      </Typography>

      <Stack spacing={2}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Global Context
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {contextStats.context?.scopes?.global?.variables || 0}
              {' '}
              variables
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Flow Context
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {contextStats.context?.scopes?.flow?.variables || 0}
              {' '}
              variables
            </Typography>
          </CardContent>
        </Card>

        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={syncContextWithBackend}
          fullWidth
        >
          Sync with Backend
        </Button>
      </Stack>
    </Box>
  );

  const renderStreaming = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Real-time Streaming
      </Typography>

      <Card>
        <CardContent sx={{ p: 2 }}>
          <FormControlLabel
            control={(
              <Switch
                checked={isStreaming}
                onChange={(e) => {
                  setIsStreaming(e.target.checked);
                  if (e.target.checked) {
                    subscribeToBackendEvents();
                  }
                }}
              />
            )}
            label="Enable Real-time Streaming"
          />

          {isStreaming && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Streaming active. Context changes and tool executions will be displayed in real-time.
              </Typography>
            </Alert>
          )}

          <Button
            size="small"
            variant="outlined"
            startIcon={<StreamIcon />}
            onClick={subscribeToBackendEvents}
            sx={{ mt: 1 }}
            fullWidth
          >
            Start Streaming
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  const renderAnalytics = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Performance Analytics
      </Typography>

      <Stack spacing={2}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">System Health</Typography>
              <Typography variant="h5" color="primary">
                {contextStats.backend?.health?.score || 0}
                %
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Active Connections</Typography>
              <Typography variant="h5" color="secondary">
                {contextStats.backend?.subscriptions?.total_connections || 0}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Button
          variant="outlined"
          startIcon={<AnalyticsIcon />}
          onClick={getEnhancedStats}
          fullWidth
        >
          Refresh Stats
        </Button>
      </Stack>
    </Box>
  );

  const renderSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Settings
      </Typography>

      <Card>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
            Configuration
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Auto-sync context"
            />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Enable real-time streaming"
            />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Show performance metrics"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  // Component now only renders in tab mode (sidebar functionality removed)
  return (
    <>
      {/* Debug Status */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">MCP Integration</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure MCP servers and manage API keys.
        </Typography>
        <Box display="flex" gap={1}>
          <Chip
            label={configDialog.open ? 'OPEN' : 'CLOSED'}
            color={configDialog.open ? 'success' : 'default'}
            size="small"
          />
          <Chip
            label={configDialog.server?.name || 'None'}
            color="info"
            size="small"
          />
        </Box>
      </Box>

      {/* Main MCP Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {renderTabContent()}
      </Box>

      {/* Dialog */}
      {configDialog.open && (
        <Dialog
          open={configDialog.open}
          onClose={() => debugSetConfigDialog({ open: false, server: null, apiKey: '' })}
        >
          <DialogTitle>Configure API Key</DialogTitle>
          <DialogContent>
            <Typography>{configDialog.server?.name || 'MCP Server'}</Typography>
            <TextField
              label="API Key"
              value={configDialog.apiKey}
              onChange={(e) => debugSetConfigDialog((prev) => ({ ...prev, apiKey: e.target.value }))}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => debugSetConfigDialog({ open: false, server: null, apiKey: '' })}>Cancel</Button>
            <Button onClick={() => { debugSetConfigDialog({ open: false, server: null, apiKey: '' }); alert('API Key saved!'); }}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default MCPFlowIntegration;
